<template>
  <div v-if="visible" class="modal-overlay" @click.self="close">
    <div class="modal-container glass-modal-container modal-resolve">
      <div class="modal-header glass-modal-header">
        <h3>Resolve Dispute</h3>
        <button class="modal-close" @click="close">×</button>
      </div>
      <div class="modal-body">
        <div class="info-card glass-card-inner">
          <div class="info-row">
            <span class="info-label">Invoice #:</span>
            <span class="info-value">{{ dispute ? dispute.invoice_id : '' }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Customer:</span>
            <span class="info-value">{{ getCustomerName() }}</span>
          </div>
          <div class="info-row">
            <span class="info-label">Dispute Type:</span>
            <span class="info-value">{{ formatDisputeType() }}</span>
          </div>
        </div>
        
        <div class="form-group">
          <label>Final Resolution *</label>
          <select v-model="resolution" class="modal-select glass-select">
            <option :value="null">Select resolution</option>
            <option value="customer_favor">Customer's Favor</option>
            <option value="business_favor">Business's Favor</option>
            <option value="partial_refund">Partial Refund</option>
            <option value="full_refund">Full Refund</option>
            <option value="replacement">Replacement</option>
            <option value="chargeback_won">Chargeback Won</option>
            <option value="chargeback_lost">Chargeback Lost</option>
            <option value="legal_action">Legal Action</option>
          </select>
        </div>
        
        <div class="form-group">
          <label>Resolution Notes</label>
          <textarea v-model="notes" rows="3" class="modal-textarea glass-input" placeholder="Add notes about how this dispute was resolved..."></textarea>
        </div>
        
        <div class="form-group">
          <label class="checkbox-label glass-checkbox">
            <input type="checkbox" v-model="moneyLost" />
            <span>Money was lost due to this dispute</span>
          </label>
        </div>
        
        <div class="form-group" v-if="moneyLost">
          <label>Money Amount Lost ($)</label>
          <input type="number" v-model="amount" class="modal-input glass-input" placeholder="Enter amount" step="0.01" />
        </div>
      </div>
      <div class="modal-footer glass-modal-footer">
        <button class="btn-secondary glass-btn" @click="close">Cancel</button>
        <button class="btn-primary glass-btn-primary" @click="submit" :disabled="!resolution">Resolve Dispute</button>
      </div>
    </div>
  </div>
</template>

<script>
import Requestor from "@/utilities/Requestor"

export default {
  name: 'ResolveModal',
  props: {
    visible: { type: Boolean, default: false },
    dispute: { type: Object, default: null }
  },
  data() {
    return {
      resolution: null,
      notes: '',
      moneyLost: false,
      amount: null
    }
  },
  watch: {
    visible(val) {
      if (val && this.dispute) {
        this.loadDisputeData()
      }
    },
    dispute: {
      handler() {
        if (this.visible && this.dispute) {
          this.loadDisputeData()
        }
      },
      deep: true
    }
  },
  methods: {
    loadDisputeData() {
      this.resolution = this.dispute.resolution && this.dispute.resolution !== 'pending' ? this.dispute.resolution : null
      this.notes = this.dispute.resolution_notes || ''
      this.moneyLost = this.dispute.money_lost || false
      this.amount = null
    },
    getCustomerName() {
      return this.dispute && this.dispute.customer ? this.dispute.customer.fullname : 'N/A'
    },
    formatDisputeType() {
      const types = {
        'customer': 'Customer',
        'credit_card': 'Credit Card',
        'snap_finance': 'Finance',
        'merchant_service': 'Merchant Service',
        'attorney': 'Attorney',
        'court': 'Court'
      }
      return this.dispute ? (types[this.dispute.dispute_type] || this.dispute.dispute_type) : ''
    },
    resetForm() {
      this.resolution = null
      this.notes = ''
      this.moneyLost = false
      this.amount = null
    },
    async submit() {
      if (!this.resolution) return
      
      const data = {
        dispute_id: this.dispute.id,
        resolution_notes: this.notes,
        final_resolution: this.resolution,
        money_lost: this.moneyLost,
        money_amount: this.moneyLost ? this.amount : null,
        resolved_by: 'Admin'
      }
      
      try {
        const response = await Requestor.postAsyncP("/api/sales/resolve_dispute/", data)
        if (response.data.error) {
          console.error(response.data.error)
        } else {
          this.$emit('resolved')
          this.close()
        }
      } catch (error) {
        console.error('Error resolving dispute:', error)
      }
    },
    close() {
      this.resetForm()
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  overflow-y: auto;
  padding: 20px;
}

.modal-container {
  width: 100%;
  max-width: 600px;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
  animation: modalSlideIn 0.3s ease;
  margin: auto;
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.5);
  border-radius: 24px;
}

@keyframes modalSlideIn {
  from { transform: translateY(-30px); opacity: 0; }
  to { transform: translateY(0); opacity: 1; }
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  background: rgba(255, 255, 255, 0.98);
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 24px 24px 0 0;
}

.modal-close {
  background: transparent;
  border: none;
  font-size: 28px;
  cursor: pointer;
  color: #94a3b8;
  width: 36px;
  height: 36px;
  border-radius: 50%;
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: rgba(255, 255, 255, 0.98);
  border-top: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 0 0 24px 24px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #334155;
  margin-bottom: 8px;
}

.info-card {
  padding: 16px;
  border-radius: 12px;
  margin-bottom: 20px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(255, 255, 255, 0.3);
}

.info-row {
  display: flex;
  margin-bottom: 10px;
  flex-wrap: wrap;
  gap: 8px;
}

.info-label {
  font-weight: 600;
  width: 120px;
  font-size: 12px;
  color: #64748b;
}

.info-value {
  flex: 1;
  font-size: 13px;
  color: #1e293b;
  font-weight: 500;
}

.modal-input,
.modal-select,
.modal-textarea {
  width: 100%;
  padding: 10px 14px;
  font-size: 14px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid rgba(128, 128, 128, 0.3);
  border-radius: 10px;
}

.modal-textarea {
  resize: vertical;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;
}

.btn-secondary {
  background: rgba(241, 245, 249, 0.9);
  color: #475569;
  border: 1px solid rgba(128, 128, 128, 0.2);
  border-radius: 10px;
  padding: 10px 20px;
  cursor: pointer;
}

.btn-primary {
  background: linear-gradient(135deg, #1e293b, #0f172a);
  color: white;
  border: none;
  border-radius: 10px;
  padding: 10px 24px;
  cursor: pointer;
}

.btn-primary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>


// WEBPACK FOOTER //
// src/components/AdminTools/DisputedOrders/components/ResolveModal.vue